import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faStar as faStarSolid,
    faSearch,
    faTimes,
    faFilter,
    faBan,
    faTimesCircle,
    faListAlt,
    faRedoAlt,
    faInbox,
    faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import "../ApplicationsDashboard/ApplicationsDashboard.css";
import "./RejectedApplicationsDashboard.css";
import FilterModal from "../Reports/FilterModal/FilterModal";
import SeedsLogo from "../../assets/images/SeedsLogo.png";
import { useDispatch, useSelector } from "react-redux";
import { getAllApplications, getApplicationStatusList } from "../../redux/ApplicationReducer/action";
import UserProfileDropdown from "../../components/UserProfile/UserProfile";
import CustomPagination from "../../components/Pagination/CustomPagination";
import Sidebar from "../../components/Sidebar/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getEligibleOfficers, getOfficers } from "../../redux/UserReducer/action";
import { OFFICER_APPLICATION_FILTERS, REJECTED_APPLICATION_COLUMN_RULES, REJECTED_APPLICATIONS_COMPONENTS } from "../../config/application";
import StageChangeModal from "../../components/StageChangeModal/StageChangeModal";
import ColumnCell from "../../components/ColumnCell/ColumnCell";
import { toast } from "react-toastify";
import axios from "axios";
import FilledButton from "../../components/Buttons/FilledButton";
import AccessWrapper from "../../components/AccessWrapper/AccessWrapper";
import { downloadFile, isAuthorised } from "../../utils/common";
import { TiArrowSortedDown } from "react-icons/ti";
import { downloadReport } from "../../utils/reportUtils";
import DownloadApplicationsModal from "../../components/DownloadApplicationsModal/DownloadApplicationsModal";
import ExportProgressBanner from "../../components/ExportProgress/ExportProgressBanner";
import { useRejectedApplicationsExport } from "../../hooks/useRejectedApplicationsExport";
import { REJECTED_APPS_EXPORT_CHUNK_THRESHOLD } from "../../utils/rejectedApplications";

let INITIAL_STAGE_CHANGE_PAYLOAD = {
    application_id: "",
    previous_stage: "",
    new_child_stage: "",
    transition_level: "CHILD",
    reason: "",
    new_parent_stage: ""
}

// The only parent stage this dashboard ever displays.
const REJECTED_PARENT_STAGE = "Reject";

const RejectedApplicationsDashboard = () => {
    const navigate = useNavigate();

    const [allowedStages, setAllowedStages] = useState([]);

    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadScope, setDownloadScope] = useState('current');
    const {
        isExporting,
        progressPercent,
        exportError,
        exportSuccess,
        startExport,
    } = useRejectedApplicationsExport();

    // Get auth token
    const authToken = useMemo(() => {
        const tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
        return tokens?.token || null;
    }, []);

    let headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-type': 'application/json'
    }

    const officer = JSON.parse(sessionStorage.getItem("officer"));

    const { applications, applicationStatusList, officerProfile, officers } = useSelector((store) => {
        return {
            applications: store.ApplicationReducer.applications.data,
            applicationStatusList: store.ApplicationReducer.applicationStatusList,
            officerProfile: store.UserReducer.officerProfile,
            officers: store.UserReducer.officers.officers
        }
    }, []);

    const dispatch = useDispatch();
    const location = useLocation();

    const storedFilters = JSON.parse(sessionStorage.getItem('rejectedApplicationFilters')) || {};

    const [rejectedApplicationFilters, setRejectedApplicationFilters] = useState({
        global_search: storedFilters?.global_search || "",
        ro_id: storedFilters?.ro_id || "",
        application_id: storedFilters?.application_id || "",
        parent_stage_name: storedFilters?.parent_stage_name || "Reject",
        // The filter panel writes the parent stage under this key too and keeps
        // the two in sync; seeding it avoids the panel showing an empty stage.
        current_application_bucket: storedFilters?.current_application_bucket || "Reject",
        application_stage_name: storedFilters?.application_stage_name || "",
        branch_name: storedFilters?.branch_name || "",
        branch_code: storedFilters?.branch_code || "",
        seeds_id: storedFilters?.seeds_id || "",
        name: storedFilters?.name || "",
        ro_name: storedFilters?.ro_name || "",
        bm_name: storedFilters?.bm_name || "",
        bcm_name: storedFilters?.bcm_name || "",
        start_date: storedFilters?.start_date || "",
        end_date: storedFilters?.end_date || "",
        rejected_start_date: storedFilters?.rejected_start_date || "",
        rejected_end_date: storedFilters?.rejected_end_date || "",
        customer_id: storedFilters?.customer_id || "",
        // Keys written by the shared OFFICER_APPLICATION_FILTERS panel. The
        // thunk maps customer_name/external_customer_id onto this endpoint's
        // name/customer_id params before the request goes out.
        customer_name: storedFilters?.customer_name || "",
        external_customer_id: storedFilters?.external_customer_id || "",
        sort_by: storedFilters?.sort_by || "",
        sort_order: storedFilters?.sort_order || "",
        ro_employee_ids: storedFilters?.ro_employee_ids || [],
        bm_employee_ids: storedFilters?.bm_employee_ids || [],
        bcm_employee_ids: storedFilters?.bcm_employee_ids || []
    });

    const [isSearchOpen, setIsSearchOpen] = useState(Boolean(storedFilters?.global_search));
    const searchInputRef = React.useRef(null);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [activeFilterTab, setActiveFilterTab] = useState('seeds_id');

    const handleClearFilters = () => {
        const clearedFilters = {
            global_search: "",
            ro_id: "",
            application_id: "",
            parent_stage_name: "Reject",
            current_application_bucket: "Reject",
            application_stage_name: "",
            branch_name: "",
            branch_code: "",
            seeds_id: "",
            name: "",
            ro_name: "",
            bm_name: "",
            bcm_name: "",
            start_date: "",
            end_date: "",
            rejected_start_date: "",
            rejected_end_date: "",
            customer_id: "",
            customer_name: "",
            external_customer_id: "",
            sort_by: "",
            sort_order: "",
            ro_employee_ids: [],
            bm_employee_ids: [],
            bcm_employee_ids: []
        };
        setRejectedApplicationFilters(clearedFilters);
        setDateRange([null, null]);
        setRejectedDateRange([null, null]);
        sessionStorage.setItem("rejectedApplicationFilters", JSON.stringify(clearedFilters));
        handlePageChange(1);
        return clearedFilters;
    };

    const getActiveFilterCount = () => {
        let count = 0;
        // seeds_id/application_id are no longer derived from global_search (see
        // handleFilterChange), so counting them plainly is safe - each one set
        // means the user set it, whether via the search box's own
        // classification or the All Filters panel directly.
        for (const [key, value] of Object.entries(rejectedApplicationFilters)) {
            if (key === 'parent_stage_name' && value === 'Reject') continue;
            if (key === 'current_application_bucket' && value === 'Reject') continue;
            if (key === 'global_search') continue;
            // ro_employee_ids/bm_employee_ids/bcm_employee_ids are arrays. An
            // empty array is never === "", so `value !== ""` counted all three
            // as active on every render - the badge could never read less than
            // 3, and it never reached 0 after Clear Filters even though the
            // filters (and the fetched data) really had been cleared.
            const hasValue = Array.isArray(value) ? value.length > 0 : value !== "";
            if (hasValue) count++;
        }
        return count;
    };

    const [dateRange, setDateRange] = useState([
        rejectedApplicationFilters?.start_date ? new Date(rejectedApplicationFilters.start_date) : null,
        rejectedApplicationFilters?.end_date ? new Date(rejectedApplicationFilters.end_date) : null,
    ]);

    const [startDate, endDate] = dateRange;

    const [rejectedDateRange, setRejectedDateRange] = useState([
        rejectedApplicationFilters?.rejected_start_date ? new Date(rejectedApplicationFilters.rejected_start_date) : null,
        rejectedApplicationFilters?.rejected_end_date ? new Date(rejectedApplicationFilters.rejected_end_date) : null,
    ]);

    const [rejectedStartDate, rejectedEndDate] = rejectedDateRange;

    const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('currentPage')) || 1);
    const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('limit')) || 10);
    const totalCount = parseInt(sessionStorage.getItem('totalCount')) || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const [openStageChangeModal, setOpenStageChangeModal] = useState(false);
    const [stageChangeObj, setStageChangeObj] = useState(INITIAL_STAGE_CHANGE_PAYLOAD);

    useEffect(() => {
          console.log("🔵 useEffect triggered with filters:", rejectedApplicationFilters);
        let cleanFilters = { ...rejectedApplicationFilters };

        if (cleanFilters.ro_employee_ids?.length === 0) delete cleanFilters.ro_employee_ids;
        if (cleanFilters.bm_employee_ids?.length === 0) delete cleanFilters.bm_employee_ids;
        if (cleanFilters.bcm_employee_ids?.length === 0) delete cleanFilters.bcm_employee_ids;

        // We no longer automatically map seeds_id to application_id if it's a UUID.
        // The user expects that pasting an Application ID into the Application Number field
        // will naturally yield 0 records instead of magically redirecting the query.
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (cleanFilters.seeds_id) {
            const trimmedSeedsId = cleanFilters.seeds_id.trim();
            if (trimmedSeedsId.includes("...")) {
                const parts = trimmedSeedsId.split("...");
                if (parts.length === 2) {
                    const firstPart = parts[0].trim();
                    const lastPart = parts[1].trim();
                    const matchedApp = applications?.find(app =>
                        app.application_id?.startsWith(firstPart) &&
                        app.application_id?.endsWith(lastPart)
                    );
                    if (matchedApp && !cleanFilters.application_id) {
                        cleanFilters.application_id = matchedApp.application_id;
                    }
                }
                cleanFilters.seeds_id = "";
            } else if (uuidRegex.test(trimmedSeedsId)) {
                // To safely ensure 0 records are returned without crashing the backend,
                // we send a dummy seeds_id that will match nothing.
                cleanFilters.seeds_id = "INVALID_EXPECTS_ZERO_RESULTS";
            }
        }
console.log("🟢 Dispatching with cleanFilters:", cleanFilters);
        dispatch(getAllApplications(currentPage, limit, cleanFilters));

        // dispatch(getEligibleOfficers(1, 2000, {}));

        sessionStorage.setItem('currentPage', currentPage);
        sessionStorage.setItem('limit', limit);
        sessionStorage.setItem('rejectedApplicationFilters', JSON.stringify(rejectedApplicationFilters));
    }, [dispatch, limit, currentPage, rejectedApplicationFilters]);

    useEffect(() => {
        dispatch(getApplicationStatusList({
            is_active: true
        }));
    }, [dispatch]);

    // Populates the RO/BM/BCM Employee IDs multiselect options in the All
    // Filters panel, same as the Deleted Applications dashboard.
    useEffect(() => {
        dispatch(getOfficers(1, 1000, { is_active: true }));
    }, [dispatch]);

    // Populates the RO/BM/BCM Employee IDs multiselect options in the All
    // Filters panel, same as the Deleted Applications dashboard.
    useEffect(() => {
        dispatch(getOfficers(1, 1000, { is_active: true }));
    }, [dispatch]);

    const handlePageChange = (newPage) => {
        // Ensure we're working with 1-based pagination
        const pageToSet = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(pageToSet);
        sessionStorage.setItem('currentPage', pageToSet.toString());
    };

    const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        const updatedFilters = {
            ...rejectedApplicationFilters,
            start_date: formatDate(start),
            end_date: formatDate(end),
        };
        setRejectedApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("rejectedApplicationFilters", JSON.stringify(updatedFilters));
    };

    const handleRejectedDateRangeChange = (update) => {
        setRejectedDateRange(update);
        const [start, end] = update;
        const updatedFilters = {
            ...rejectedApplicationFilters,
            rejected_start_date: formatDate(start),
            rejected_end_date: formatDate(end),
        };
        setRejectedApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("rejectedApplicationFilters", JSON.stringify(updatedFilters));
    };

    const handleFilterChange = (e) => {
        let { name, value } = e.target;

        // Clean up accidental tabs/newlines and leading spaces from copy-pasting from tables
        if (typeof value === 'string') {
            value = value.replace(/[\t\n\r]+/g, '').replace(/^\s+/, '');
        }

        let updatedFilters = {
            ...rejectedApplicationFilters,
            [name]: value
        };

        if (name !== 'global_search') {
            updatedFilters.global_search = '';
        } else {
            // The top search box only ever sets global_search. It used to also
            // guess whether the text was an Application ID or an Application
            // Number and write that guess into the panel's own fields - using a
            // simpler UUID-only check than the one the dispatch thunk actually
            // uses (which also recognizes RO IDs, branch names, etc). The two
            // disagreed on inputs like "SFC00012", so the Application Number
            // field would show a value the search never intended, and that
            // guess outlived the search box (nothing cleared it going forward).
            // getAllApplications already re-classifies global_search into the
            // right filter at dispatch time without mutating what's on screen,
            // so this box no longer needs its own copy of that logic - it
            // clears the panel's explicit fields (search supersedes them) and
            // leaves classification to the one place that does it correctly.
            updatedFilters = {
                ...updatedFilters,
                seeds_id: '',
                application_id: '',
                branch_name: '',
                branch_code: '',
                application_stage_name: '',
                name: '',
                ro_name: '',
                bm_name: '',
                bcm_name: '',
                customer_id: '',
                customer_name: '',
                external_customer_id: '',
                ro_employee_ids: [],
                bm_employee_ids: [],
                bcm_employee_ids: []
            };
        }

        // If parent stage changes → reset application stage & sync parent_stage_name/current_application_bucket
        if (name === "parent_stage_name" || name === "current_application_bucket") {
            updatedFilters.parent_stage_name = value;
            updatedFilters.current_application_bucket = value;
            updatedFilters.application_stage_name = "";
        }

        setRejectedApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("rejectedApplicationFilters", JSON.stringify(updatedFilters));
    }

    const masterStatusList = applicationStatusList || [];

    // This screen shows rejected applications only, so Parent Stage is fixed to
    // Reject. It used to list every parent stage in the master, letting the user
    // pick one that contradicts the screen and returns nothing.
    const parentStageOptions = React.useMemo(() => {
        const fromMaster = [
            ...new Set(masterStatusList?.map(item => item.parent_stage_name))
        ].filter(name => name === REJECTED_PARENT_STAGE);

        // Offer the option even before the master list has come back.
        return fromMaster.length ? fromMaster : [REJECTED_PARENT_STAGE];
    }, [masterStatusList]);

    // Application Stages for the selected parent. The parent is pinned to Reject,
    // so an unset value must still scope to Reject's stages - falling back to
    // every stage in the system would offer stages this screen can never show.
    const applicationStageOptions = React.useMemo(() => {
        const parentStage =
            rejectedApplicationFilters.parent_stage_name || REJECTED_PARENT_STAGE;

        return [
            ...new Set(
                masterStatusList
                    ?.filter(item => item.parent_stage_name === parentStage)
                    ?.map(item => item.application_stage_name)
            )
        ];
    }, [masterStatusList, rejectedApplicationFilters.parent_stage_name]);

    // RO/BM/BCM Employee IDs options for the All Filters panel, grouped from the
    // officer list by role - same construction as Deleted Applications.
    const officerOptions = React.useMemo(() => {
        const roOptions = [];
        const bmOptions = [];
        const bcmOptions = [];

        officers?.forEach(branch => {
            branch?.officer_details?.forEach(officer => {
                const option = {
                    label: `${officer.emp_id} - ${officer.first_name} ${officer.last_name || ""}`.trim(),
                    value: officer.emp_id
                };

                if (officer.role_code === "RO") {
                    roOptions.push(option);
                }

                if (officer.role_code === "BM") {
                    bmOptions.push(option);
                }

                if (officer.role_code === "BCM") {
                    bcmOptions.push(option);
                }
            });
        });

        // remove duplicate emp_ids
        const uniqueByValue = (arr) => {
            return arr.filter(
                (item, index, self) =>
                    index === self.findIndex(obj => obj.value === item.value)
            );
        };

        return {
            roOptions: uniqueByValue(roOptions),
            bmOptions: uniqueByValue(bmOptions),
            bcmOptions: uniqueByValue(bcmOptions),
        };
    }, [officers]);

    // Branch Code options for the All Filters panel
    const branchOptions = React.useMemo(() => {
        if (!officers || !Array.isArray(officers)) return [];

        const options = officers
            .filter(branch => branch?.branch_code)
            .map(branch => ({
                label: `${branch.branch_code} - ${branch.branch_name}`,
                value: branch.branch_code
            }));

        // remove duplicate branch_codes
        return options.filter(
            (item, index, self) =>
                index === self.findIndex(obj => obj.value === item.value)
        );
    }, [officers]);

    // OFFICER_APPLICATION_FILTERS is shared with the Branch and Team screens,
    // where clearing the parent stage is meaningful and Application Owner is a
    // real filter. Here it is the one filter that must always hold a value, so
    // the field is marked non-clearable for this screen only rather than in the
    // shared config. Application Owner is dropped entirely: this screen's
    // endpoint (getAllApplication) never reads that param, so the filter was a
    // silent no-op, and every value but "Rejected" contradicts a screen that
    // only ever shows rejections.
    const rejectedFilterFields = React.useMemo(
        () =>
            OFFICER_APPLICATION_FILTERS
                .filter(field => field.key !== "application_owner")
                .map(field =>
                    field.key === "current_application_bucket"
                        ? { ...field, allowUnselect: false }
                        : field
                ),
        []
    );

    const handleParentStageChipClick = (stage) => {
        const updatedFilters = {
            ...rejectedApplicationFilters,
            parent_stage_name: rejectedApplicationFilters.parent_stage_name === stage ? "" : stage,
            application_stage_name: "",
        };
        setRejectedApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("rejectedApplicationFilters", JSON.stringify(updatedFilters));
    };

    const handleStageChange = async (obj) => {

        let stageChangePayload = {
            reason: obj?.reason,
            application_id: obj?.application_id,
        }

        console.log({ stageChangePayload });

        try {
            let response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/applications/reenable-application`, stageChangePayload, { headers });

            dispatch(getAllApplications(1, limit, rejectedApplicationFilters));
            handlePageChange(1);
            setStageChangeObj(INITIAL_STAGE_CHANGE_PAYLOAD);
            setOpenStageChangeModal(false);

        } catch (error) {
            toast.error(`${error?.response?.data?.message}`);
        }
    }

    const resetFilters = () => {
        setStageChangeObj(INITIAL_STAGE_CHANGE_PAYLOAD);
    }

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
        ];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };
    console.log({ loc: location.pathname });
    console.log("🔴 RENDER — filters:", JSON.stringify(rejectedApplicationFilters));

    return (
        <div className="applicationsDashboardContainer">
            <div className="applicationsDashboard">
                <Sidebar />

                <div className="dashboardMainContent">
                    {/* --- Top Header --- */}
                    <div className="contentHeader">
                        <div className="userLogo">
                            <img src={SeedsLogo} alt="logo" className="SeedsLogo" />
                        </div>
                        <div className="headerRightPanel">
                            <UserProfileDropdown />
                        </div>
                    </div>

                    {/* --- Page Content --- */}
                    <div className="pageContentWrapper">
                        <div className="stickyHeaderContainer">
                            {/* Title */}
                            <div className="pageHeaderSection">
                                <div className="pageActions">
                                    <h2>Rejected Applications</h2>
                                    
                                </div>
                            </div>

                            {/* --- Search & Filter Bar --- */}
                            <div className="searchFilterBar insuranceSearchFilterBar">
                                <div className="insuranceSearchGroup">
                                    <div className="searchInputWrapper">
                                        <FontAwesomeIcon icon={faSearch} className="searchIcon" />
                                        <input
                                            type="search"
                                            name="global_search"
                                            value={rejectedApplicationFilters?.global_search || ''}
                                            placeholder="Search Rejected Applications..."
                                            onChange={handleFilterChange}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    dispatch(getAllApplications(1, limit, rejectedApplicationFilters));
                                                }
                                            }}
                                        />
                                    </div>
                                    <button
                                        className="insuranceSearchBtn"
                                        onClick={() => {
                                            dispatch(getAllApplications(1, limit, rejectedApplicationFilters));
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faSearch} />
                                        <span>Search</span>
                                    </button>
                                    {getActiveFilterCount() > 0 && (
                                        <button
                                            className="clearFiltersBtn"
                                            onClick={handleClearFilters}
                                            title="Clear Filters"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                            <span>Clear Filters</span>
                                        </button>
                                    )}
                                    <div className="filterPopoverWrapper">
                                        <button
                                            className="allFiltersBtn"
                                            onClick={() => setShowFilterModal(!showFilterModal)}
                                            title="All Filters"
                                        >
                                            <FontAwesomeIcon icon={faFilter} />
                                            <span>All Filters</span>
                                            {getActiveFilterCount() > 0 && (
                                                <span className="activeFilterCount">
                                                    {getActiveFilterCount()}
                                                </span>
                                            )}
                                        </button>

                                        {/* --- Advanced Filters Popover Panel --- */}
                                        {showFilterModal && (
                                            <FilterModal
                                                reportTitle="applications"
                                                moduleName="Officer Applications"
                                                fields={rejectedFilterFields}
                                                filters={rejectedApplicationFilters}
                                                setFilters={setRejectedApplicationFilters}
                                                resetFilters={handleClearFilters}
                                                handleApplyFilters={() => {
                                                    handlePageChange(1);
                                                    setShowFilterModal(false);
                                                }}
                                                onClose={() => setShowFilterModal(false)}
                                                dynamicOptions={{
                                                    current_application_bucket: parentStageOptions,
                                                    application_stage_name: applicationStageOptions,
                                                    branch_code: branchOptions,
                                                    ro_employee_ids: officerOptions.roOptions,
                                                    bm_employee_ids: officerOptions.bmOptions,
                                                    bcm_employee_ids: officerOptions.bcmOptions,
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                        {
                                            isAuthorised(
                                                officerProfile,
                                                REJECTED_APPLICATIONS_COMPONENTS?.download
                                            ) &&
                                            // <button 
                                            //     className="downloadButton"
                                            //     onClick={() => {
                                            //          let endpoint = `${process.env.REACT_APP_API_BASE_URL}/applications/get-all-applications`;

                                            //         downloadFile(`Rejected_Applications`, endpoint, rejectedApplicationFilters, "xlsx");
                                            //     }}
                                            // >
                                            //     Download Excel
                                            //     <TiArrowSortedDown style={{ width: "16px", height: "16px" }}/>
                                            // </button>
                                            <button
                                                className="downloadButton"
                                                disabled={isExporting}
                                                onClick={() => setShowDownloadModal(true)}
                                            >
                                                Download Excel
                                                <TiArrowSortedDown style={{ width: "16px", height: "16px" }} />
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- Data Table --- */}
                        <div className="tableCard">
                            <div className="applicationsTableContainer">
                                <table className="applications-table">
                                    <thead>
                                        <tr>
                                            <th className={rejectedApplicationFilters?.seeds_id ? 'filter-applied' : ''}>Application Number</th>
                                            <th className={rejectedApplicationFilters?.ro_name ? 'filter-applied' : ''}>RO Name</th>
                                            <th className={rejectedApplicationFilters?.ro_id ? 'filter-applied' : ''}>RO ID</th>
                                            <th className={rejectedApplicationFilters?.application_id ? 'filter-applied' : ''}>Application ID</th>
                                            <th className={(rejectedApplicationFilters?.start_date || rejectedApplicationFilters?.end_date) ? 'filter-applied' : ''}>Application Date</th>
                                            <th className={(rejectedApplicationFilters?.rejected_start_date || rejectedApplicationFilters?.rejected_end_date) ? 'filter-applied' : ''}>Rejected Date</th>
                                            <th className={rejectedApplicationFilters?.parent_stage_name && rejectedApplicationFilters?.parent_stage_name !== 'Reject' ? 'filter-applied' : ''}>Parent Stage</th>
                                            <th className={rejectedApplicationFilters?.application_stage_name ? 'filter-applied' : ''}>Application Stage</th>
                                            <th>Rejection Reason</th>
                                            <th className={(rejectedApplicationFilters?.customer_name || rejectedApplicationFilters?.name) ? 'filter-applied' : ''}>Customer Name</th>
                                            <th className={(rejectedApplicationFilters?.external_customer_id || rejectedApplicationFilters?.customer_id) ? 'filter-applied' : ''}>Customer ID</th>
                                            <th className={rejectedApplicationFilters?.branch_code ? 'filter-applied' : ''}>Branch ID</th>
                                            <th className={rejectedApplicationFilters?.branch_name ? 'filter-applied' : ''}>Branch</th>
                                            <th className={rejectedApplicationFilters?.bm_name ? 'filter-applied' : ''}>BM Name</th>
                                            <th>BM ID</th>
                                            <th className={rejectedApplicationFilters?.bcm_name ? 'filter-applied' : ''}>BCM Name</th>
                                            <th>BCM ID</th>
                                            <th style={{ textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            !applications || applications?.length === 0 ?
                                                (
                                                    <tr>
                                                        <td colSpan={17} className="rejectedNoDataCell">
                                                            <div className="rejectedNoDataContent">
                                                                <FontAwesomeIcon
                                                                    icon={faInbox}
                                                                    className="rejectedNoDataIcon"
                                                                />
                                                                <span className="rejectedNoDataTitle">
                                                                    No rejected applications found
                                                                </span>
                                                                <span className="rejectedNoDataSubtitle">
                                                                    Try adjusting your filters or search criteria to find the applications you're looking for.
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) :
                                                applications?.map((application, index) => {
                                                    const roLabel = [application?.ro?.first_name?.trim(), application?.ro?.middle_name?.trim(), application?.ro?.last_name?.trim()].filter(Boolean).join(" ");
                                                    const bmLabel = [application?.bm?.first_name?.trim(), application?.bm?.middle_name?.trim(), application?.bm?.last_name?.trim()].filter(Boolean).join(" ");
                                                    const bcmLabel = [application?.bcm?.first_name?.trim(), application?.bcm?.middle_name?.trim(), application?.bcm?.last_name?.trim()].filter(Boolean).join(" ");
                                                    const rawReason = application?.rejected_reason || "";
                                                    const displayReason = rawReason || "—";

                                                    return (
                                                        <tr key={application?.application_id}>
                                                            <td>
                                                                <span
                                                                    className="rejectedAppLink"
                                                                    onClick={() => {
                                                                        const selectedText = window.getSelection().toString();
                                                                        if (!selectedText) {
                                                                            navigate(`/applications/${application.application_id}?from=${encodeURIComponent(location.pathname)}`);
                                                                        }
                                                                    }}
                                                                >
                                                                    {application.seeds_id}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <ColumnCell
                                                                    officer={officer}
                                                                    columnKey="ro_name"
                                                                    moduleConfig={REJECTED_APPLICATION_COLUMN_RULES}
                                                                    fallback={<span>{roLabel}</span>}
                                                                />
                                                            </td>
                                                            <td>{application?.ro?.emp_id}</td>
                                                            <td>{application?.application_id}</td>
                                                            <td>
                                                                <span className="rejectedDateText">
                                                                    {formatDisplayDate(application?.createdAt?.split('T')?.[0])}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="rejectedDateText"
                                                                >
                                                                    {formatDisplayDate(application.rejected_at?.split('T')?.[0])}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="rejectedStatusBadge rej-status-reject">
                                                                    <span className="statusDot"></span>
                                                                    {application.parent_stage_name}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="rejectedStatusBadge rej-status-default">
                                                                    <span className="statusDot"></span>
                                                                    {application.application_stage_name}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="rejectionReasonCell"
                                                                    title={application?.rejected_reason || ""}
                                                                >
                                                                    {displayReason}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="stackedCell">
                                                                    <span className="primaryText">
                                                                        {`${application.first_name}${application?.middle_name ? ` ${application?.middle_name}` : ''}${application?.last_name ? ` ${application?.last_name}` : ''}`}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{application?.external_customer_id}</td>
                                                            <td>{application?.branch_code}</td>
                                                            <td>
                                                                <div className="stackedCell">
                                                                    <span className="primaryText">{application?.branch_name}</span>
                                                                    <span className="secondaryText">{application?.branch_code}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <ColumnCell
                                                                    officer={officer}
                                                                    columnKey="bm_name"
                                                                    moduleConfig={REJECTED_APPLICATION_COLUMN_RULES}
                                                                    displayText={bmLabel}
                                                                    fallback={<span>{bmLabel}</span>}
                                                                />
                                                            </td>
                                                            <td>{application?.bm?.emp_id}</td>
                                                            <td>
                                                                <ColumnCell
                                                                    officer={officer}
                                                                    columnKey="bcm_name"
                                                                    moduleConfig={REJECTED_APPLICATION_COLUMN_RULES}
                                                                    fallback={<span>{bcmLabel}</span>}
                                                                />
                                                            </td>
                                                            <td>{application?.bcm?.emp_id}</td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {
                                                                    isAuthorised(officer, REJECTED_APPLICATION_COLUMN_RULES?.re_enable) ?
                                                                        <button
                                                                            className="reenableButton"
                                                                            onClick={() => {
                                                                                setStageChangeObj(prev => ({
                                                                                    ...prev,
                                                                                    application_id: application?.application_id,
                                                                                    previous_stage: stageChangeObj?.transition_level === "CHILD" ? application?.application_stage_name : application?.parent_stage_name
                                                                                }))
                                                                                setOpenStageChangeModal(true);
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faRedoAlt} style={{ fontSize: '11px' }} />
                                                                            Re-enable
                                                                        </button> :
                                                                        <div></div>
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                                )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* --- Stage Change Modal --- */}
                        {
                            openStageChangeModal && (
                                <StageChangeModal
                                    obj={stageChangeObj}
                                    setObj={setStageChangeObj}
                                    onClose={() => {
                                        resetFilters();
                                        setOpenStageChangeModal(false);
                                    }}
                                    handleSubmit={handleStageChange}
                                />
                            )
                        }

                        {/* --- Pagination --- */}
                        {applications?.length > 0 && <div className="paginationControls">
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>}
                    </div>
                </div>
            </div>
            {/* ---- Download Modal (Current Page / All Pages) ---- */}
            <DownloadApplicationsModal
                show={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                downloadScope={downloadScope}
                onScopeChange={(e) => setDownloadScope(e.target.value)}
                filtersActive={Object.entries(rejectedApplicationFilters || {}).some(([key, val]) => {
                    if (key === "global_search") return false;
                    if (key === "parent_stage_name" && val === "Reject") return false;
                    return val !== "" && val !== null && val !== undefined;
                })}
                title="Download Rejected Applications"
                onDownloadClick={() => {
                    startExport(
                        downloadScope,
                        applications,
                        rejectedApplicationFilters
                    );
                    setShowDownloadModal(false);
                }}
                isExporting={isExporting}
                progressPercent={progressPercent}
                exportError={exportError}
            />

            {/* ---- Export Progress Banner ---- */}
            <ExportProgressBanner
                isExporting={isExporting}
                exportError={exportError}
                progressPercent={progressPercent}
                isLargeExport={
                    downloadScope === "all" &&
                    totalCount > REJECTED_APPS_EXPORT_CHUNK_THRESHOLD
                }
                onRetry={() => {
                    startExport(
                        downloadScope,
                        applications,
                        rejectedApplicationFilters
                    );
                }}
                exportSuccess={exportSuccess}
            />
        </div>

    );
};

export default RejectedApplicationsDashboard;
